import React from "react"
import {
  CLIENT_CONSUMPTION_REPORT_EMPTY_CHART_MESSAGE,
  CLIENT_REPORT_EMPTY_CHART_MESSAGE,
} from "../../../consts/charts.consts"
import PieChartCompound from "../../../components/compounds/graphics/PieChart.compound"
import SquareRoundedColorListLegend from "../../../components/compounds/graphics/components/SquareRoundedColorListLegend"
import useHighlightChartData from "../../../hooks/useHighlightChartData.hook"
import { numberFormatter } from "../../../utils/numbers.utils"
import useScreenDimensionsContext from "../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import DataTable from "../../../components/atoms/table/DataTable.atom"
import Divider from "../../../components/atoms/Divider.atom"
import { OPACITY_HIGHLIGHT_LEVELS } from "../../../consts/modules/graphics.consts"
import { CLIENT_REPORT_CONSUMPTION_DISTRIBUTION_CARD_MODES } from "../../../consts/modules/clientReport.consts"
import { padFloat } from "../../../utils/strings.utils"

const ClientPeriodConsumptionDistributionChart = ({
  data: chartData,
  loading = false,
  chartContainerConfig = {
    shadow: false,
    className:
      "w-full mx-auto p-0 flex-1 flex-shrink-0 client-period-consumption-distribution-chart justify-content-center",
  },
  responsiveContainerConfig = {
    minHeight: "220px",
    maxHeight: "220px",
    minWidth: "420px",
    maxWidth: "420px",
  },
  legend = true,
  legendTemplate: customLegendTemplate,
  legendConfig = {
    verticalAlign: "middle",
    align: "right",
    layout: "vertical",
  },
  width = 220,
  height = 220,
  showEmptyMessage = false,
  emptyMessageConfig = CLIENT_CONSUMPTION_REPORT_EMPTY_CHART_MESSAGE,
  startAngle = -270,
  tooltip = true,
  tooltipTemplate: customTooltipTemplate,
  selectedMode = CLIENT_REPORT_CONSUMPTION_DISTRIBUTION_CARD_MODES.BY_AREA,
}) => {
  const { data, handleMouseEnterLegend, handleMouseLeaveLegend } =
    useHighlightChartData({
      initialData: chartData,
      highlightLevels: {
        highlighted: OPACITY_HIGHLIGHT_LEVELS.HIGHLIGHTED,
        notHighlighted: OPACITY_HIGHLIGHT_LEVELS.NOT_HIGHLIGHTED_LOW,
      },
      highlightFilters: {
        highlighted: "saturate(1)",
        notHighlighted:
          (
            selectedMode ===
            CLIENT_REPORT_CONSUMPTION_DISTRIBUTION_CARD_MODES.BY_PROJECT
          ) ?
            "saturate(0)"
          : "saturate(1)",
      },
    })

  responsiveContainerConfig = {
    ...responsiveContainerConfig,
    maxWidth:
      loading ?
        responsiveContainerConfig.maxHeight
      : responsiveContainerConfig.maxWidth,
    minWidth:
      loading ?
        responsiveContainerConfig.minHeight
      : responsiveContainerConfig.minWidth,
  }
  const legendTemplate = (data) => {
    if (customLegendTemplate) {
      return customLegendTemplate(data)
    }
    return (
      <SquareRoundedColorListLegend
        data={data}
        onMouseEnter={handleMouseEnterLegend}
        onMouseLeave={handleMouseLeaveLegend}
        containerClassName="flex flex-column gap-2"
      />
    )
  }
  const tooltipTemplate = (data) => {
    if (customTooltipTemplate) {
      return customTooltipTemplate(data)
    }
    return (
      <DistributionByAreaTooltipTemplate
        value={data?.value}
        color={data?.color}
        label={data?.extraInfo?.label || data?.name}
        teamMembers={data?.extraInfo?.teamMembers}
      />
    )
  }
  return (
    <div className="flex gap-3 flex-wrap justify-content-center">
      <PieChartCompound
        data={data}
        responsiveContainerConfig={responsiveContainerConfig}
        chartContainerConfig={chartContainerConfig}
        legend={legend}
        legendTemplate={legendTemplate}
        legendConfig={legendConfig}
        tooltip={tooltip}
        tooltipTemplate={tooltipTemplate}
        label={false}
        width={width}
        height={height}
        showEmptyMessage={showEmptyMessage}
        emptyMessageConfig={emptyMessageConfig}
        startAngle={startAngle}
        loading={loading}
      />
    </div>
  )
}

const DistributionByAreaTooltipTemplate = ({
  value,
  color,
  label,
  teamMembers = [],
}) => {
  return (
    <div className="custom-recharts-tooltip flex flex-column gap-3 text-white">
      <div className="flex gap-2 align-items-center">
        <div
          className="flex align-items-center justify-content-center tendency-chip"
          style={{
            background: color,
            padding: "3.5px",
            aspectRatio: "1 / 1",
            borderRadius: "6px",
          }}
        ></div>
        <span>{label}</span>
      </div>
      <div className="flex flex-column w-10rem">
        {teamMembers.map((member, index) => (
          <React.Fragment key={index}>
            <div className="flex align-items-center justify-content-between">
              <div>{member.nameTag}</div>
              <div className="text-right">
                {padFloat(member.value, 2, "0")} hs
              </div>
            </div>
            <Divider stroke="sm" className="w-full my-2" />
          </React.Fragment>
        ))}
        <div className="flex align-items-center justify-content-between">
          <span>Total</span>
          <span className="text-right">{padFloat(value, 2, "0")} hs</span>
        </div>
      </div>
    </div>
  )
}

export default ClientPeriodConsumptionDistributionChart
